import { useReducer, useMemo, useCallback, Reducer } from 'react';

import {
  getLastDescendant,
  getNextVisibleNodeIndex,
  getPreviousVisibleNodeIndex,
  checkIsDescendant,
  relocateSubArray,
  getExpandShowState,
  expandCollapseNode,
  getCollapseShowState,
  getCollapseAllState,
  hasFilteredChildren,
} from './helpers';
import { TREE_ACTIONS } from '../consts';
import normalizeTreeInput from '../helpers/normalizeTreeInput';
import {
  IdType,
  InternalDataNode,
  DefaultInputTreeNode,
  TreeState,
  DefaultAction,
  BaseAction,
  TraverseArgs,
} from '../types';

type UseTreeReducer<
  InputTreeNode extends DefaultInputTreeNode,
  CustomAction extends DefaultAction = BaseAction<InputTreeNode>,
> = {
  denormalizedTree: InputTreeNode[];
  getIdFromNode?: (node: InputTreeNode) => IdType;
  getLabelFromNode?: (node: InputTreeNode) => string;
  getChildrenFromNode?: (node: InputTreeNode) => InputTreeNode[] | undefined;
  initialExpandAll?: boolean;
  initialHighlightedId?: IdType;
  initialExpandedIds?: Record<IdType, boolean>;
  customReducer?: Reducer<
    TreeState<InputTreeNode>,
    BaseAction<InputTreeNode> | CustomAction
  >;
};

const arraysEqual = (a: unknown[], b: unknown[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const normalizedTreesEqual = <T extends DefaultInputTreeNode>(
  a: Record<IdType, InternalDataNode<T>>,
  b: Record<IdType, InternalDataNode<T>>,
  sharedIds: IdType[],
): boolean => {
  for (const id of sharedIds) {
    const nodeA = a[id];
    const nodeB = b[id];
    if (nodeA.label !== nodeB.label || nodeA.original !== nodeB.original) {
      return false;
    }
  }
  return true;
};

const expandNodeTraverse = <InputTreeNode extends DefaultInputTreeNode>({
  currentNode,
  expandedIds,
  updatedNodes,
  normalizedTree,
}: TraverseArgs<InputTreeNode>) => {
  if (currentNode?.childIds && expandedIds[currentNode.id]) {
    currentNode?.childIds?.forEach((childId) => {
      const hasFilteredSiblings = hasFilteredChildren({
        node: currentNode,
        normalizedTree,
      });

      const childNode = normalizedTree[childId];
      updatedNodes[childId] = {
        ...childNode,
        ...getExpandShowState({
          node: childNode,
          overrideFilter: !hasFilteredSiblings,
        }),
      };
      expandNodeTraverse({
        currentNode: childNode,
        expandedIds,
        updatedNodes,
        normalizedTree,
      });
    });
  }
};

const expandNodeTraverseRecursive = <
  InputTreeNode extends DefaultInputTreeNode,
>({
  currentNode,
  expandedIds,
  updatedNodes,
  normalizedTree,
}: TraverseArgs<InputTreeNode>) => {
  currentNode?.childIds?.forEach((childId) => {
    const hasFilteredSiblings = hasFilteredChildren({
      node: currentNode,
      normalizedTree,
    });

    const childNode = normalizedTree[childId];
    updatedNodes[childId] = {
      ...childNode,
      ...getExpandShowState({
        node: childNode,
        overrideFilter: !hasFilteredSiblings,
      }),
    };
    if (childNode.childIds) {
      expandedIds[childId] = true;
    }
    expandNodeTraverseRecursive({
      currentNode: childNode,
      expandedIds,
      updatedNodes,
      normalizedTree,
    });
  });
};

const collapseNodeTraverse = <InputTreeNode extends DefaultInputTreeNode>({
  currentNode,
  expandedIds,
  updatedNodes,
  normalizedTree,
}: TraverseArgs<InputTreeNode>) => {
  currentNode?.childIds?.forEach((childId) => {
    const childNode = normalizedTree[childId];
    updatedNodes[childId] = {
      ...childNode,
      ...getCollapseShowState({ node: childNode }),
    };
    collapseNodeTraverse({
      currentNode: childNode,
      expandedIds,
      updatedNodes,
      normalizedTree,
    });
  });
};

const collapseNodeTraverseRecursive = <
  InputTreeNode extends DefaultInputTreeNode,
>({
  currentNode,
  expandedIds,
  updatedNodes,
  normalizedTree,
}: TraverseArgs<InputTreeNode>) => {
  currentNode?.childIds?.forEach((childId) => {
    const childNode = normalizedTree[childId];
    updatedNodes[childId] = {
      ...childNode,
      ...getCollapseShowState({ node: childNode }),
    };
    if (expandedIds[childId]) {
      expandedIds[childId] = false;
    }
    collapseNodeTraverseRecursive({
      currentNode: childNode,
      expandedIds,
      updatedNodes,
      normalizedTree,
    });
  });
};

const collapseAllNodes = <InputTreeNode extends DefaultInputTreeNode>({
  treeState,
  normalizedTree,
}: {
  treeState: TreeState<InputTreeNode>;
  normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>>;
}) => {
  const updatedNodes = treeState.orderedIds
    .map((nodeId) => ({
      ...normalizedTree[nodeId],
      ...getCollapseAllState({ node: normalizedTree[nodeId] }),
    }))
    .reduce(
      (accum, node) => {
        accum[node.id] = node;

        return accum;
      },
      {} as Record<IdType, InternalDataNode<InputTreeNode>>,
    );
  return {
    ...treeState,
    normalizedTree: {
      ...normalizedTree,
      ...updatedNodes,
    },
    expandedIds: {},
  };
};

const highlightNode = <InputTreeNode extends DefaultInputTreeNode>({
  treeState,
  nodeId,
}: {
  treeState: TreeState<InputTreeNode>;
  nodeId: IdType | undefined;
}) => {
  if (treeState.highlightedId === nodeId) {
    return treeState;
  }

  const updatedState: Partial<TreeState<InputTreeNode>> = {
    highlightedId: nodeId,
  };
  return {
    ...treeState,
    ...updatedState,
  };
};

const moveDown = <InputTreeNode extends DefaultInputTreeNode>({
  treeState,
  normalizedTree,
  nodeId,
}: {
  treeState: TreeState<InputTreeNode>;
  normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>>;
  nodeId: IdType | undefined;
}) => {
  const nextIndex = nodeId
    ? getNextVisibleNodeIndex({
        currentNodeId: nodeId,
        orderedIds: treeState.orderedIds,
        normalizedTree,
      })
    : 0;

  const updatedState: Partial<TreeState<InputTreeNode>> = {};
  if (nextIndex < treeState.orderedIds.length) {
    updatedState.highlightedId = treeState.orderedIds[nextIndex];
  }

  return {
    ...treeState,
    ...updatedState,
  };
};

const moveUp = <InputTreeNode extends DefaultInputTreeNode>({
  treeState,
  normalizedTree,
  nodeId,
}: {
  treeState: TreeState<InputTreeNode>;
  normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>>;
  nodeId: IdType | undefined;
}) => {
  const nextIndex = nodeId
    ? getPreviousVisibleNodeIndex({
        currentNodeId: nodeId,
        orderedIds: treeState.orderedIds,
        normalizedTree,
      })
    : treeState.orderedIds.length - 1;

  const updatedState: Partial<TreeState<InputTreeNode>> = {};
  if (nextIndex > -1) {
    updatedState.highlightedId = treeState.orderedIds[nextIndex];
  }

  return {
    ...treeState,
    ...updatedState,
  };
};

const filterNodes = <InputTreeNode extends DefaultInputTreeNode>({
  treeState,
  normalizedTree,
  nodeIds,
}: {
  treeState: TreeState<InputTreeNode>;
  normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>>;
  nodeIds: IdType[] | undefined;
}) => {
  if (!nodeIds) {
    return treeState;
  }

  const expandedIds: Record<IdType, boolean> = {};

  const treeAllNodesHidden = treeState.orderedIds.reduce(
    (newTree, id) => {
      newTree[id] = {
        ...normalizedTree[id],
        show: false,
        filterShow: false,
      };

      return newTree;
    },
    {} as Record<IdType, InternalDataNode<InputTreeNode>>,
  );

  const treeFilteredNodesAndParents = nodeIds.reduce(
    (accumNodes, id) => {
      const parentNodes = normalizedTree[id].path.reduce(
        (accum, parentId) => {
          expandedIds[parentId] = true;

          accum[parentId] = {
            ...normalizedTree[parentId],
            show: true,
            filterShow: true,
          };

          return accum;
        },
        {} as Record<IdType, InternalDataNode<InputTreeNode>>,
      );

      Object.keys(parentNodes).forEach((key) => {
        accumNodes[key] = parentNodes[key];
      });

      accumNodes[id] = {
        ...normalizedTree[id],
        show: true,
        filterShow: true,
      };

      return accumNodes;
    },
    {} as Record<IdType, InternalDataNode<InputTreeNode>>,
  );

  return {
    ...treeState,
    expandedIds,
    normalizedTree: {
      ...treeAllNodesHidden,
      ...treeFilteredNodesAndParents,
    },
  };
};

const moveNode = <InputTreeNode extends DefaultInputTreeNode>({
  treeState,
  normalizedTree,
  sourceId,
  targetId,
}: {
  treeState: TreeState<InputTreeNode>;
  normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>>;
  sourceId: IdType;
  targetId: IdType;
}) => {
  const sourceNode = normalizedTree[sourceId];
  const targetNode = normalizedTree[targetId];
  const targetIsSameAsSource = sourceId === targetId;
  const targetIsDescendantOfSource = checkIsDescendant({
    node: sourceNode,
    potentialDescendantNode: targetNode,
    normalizedTree,
  });
  if (
    !sourceNode ||
    !targetNode ||
    targetIsSameAsSource ||
    targetIsDescendantOfSource
  ) {
    return treeState;
  }
  const sourceParentNode =
    normalizedTree[sourceNode.path[sourceNode.path.length - 1]];
  const targetParentNode =
    normalizedTree[targetNode.path[targetNode.path.length - 1]];

  // relocate source and all of its descendants
  const lastSourceDescendantNode = getLastDescendant({
    node: sourceNode,
    normalizedTree,
  });
  const [updatedOrderedIds, sourceIds] = relocateSubArray({
    arr: treeState.orderedIds,
    startIndex: treeState.orderedIds.indexOf(sourceNode.id),
    endIndex: treeState.orderedIds.indexOf(lastSourceDescendantNode.id),
    targetIndex: treeState.orderedIds.indexOf(targetNode.id),
  });
  const updatedNodes = {
    [sourceNode.id]: sourceNode,
  };
  // update path of all descendants to point to new target parent
  const newPrePath = targetNode.path;
  sourceIds.forEach((id) => {
    if (id === sourceNode.id) {
      updatedNodes[sourceNode.id].path = newPrePath;
    } else {
      updatedNodes[id] = normalizedTree[id];
      updatedNodes[id].path = [
        ...newPrePath,
        ...updatedNodes[id].path.slice(
          updatedNodes[id].path.indexOf(sourceNode.id),
        ),
      ];
    }
  });

  // update old parent of source's childIds
  if (sourceParentNode) {
    updatedNodes[sourceParentNode.id] = sourceParentNode;
    const indexOfSourceInSourceParentChildIds = updatedNodes[
      sourceParentNode.id
    ]?.childIds.indexOf(sourceNode.id);
    updatedNodes[sourceParentNode.id].childIds = [
      ...updatedNodes[sourceParentNode.id].childIds.slice(
        0,
        indexOfSourceInSourceParentChildIds,
      ),
      ...updatedNodes[sourceParentNode.id].childIds.slice(
        indexOfSourceInSourceParentChildIds + 1,
      ),
    ];
  }
  // update new parent of source's childIds
  if (targetParentNode) {
    updatedNodes[targetParentNode.id] = sourceParentNode;
    const indexOfTargetInTargetParentChildIds = updatedNodes[
      targetParentNode.id
    ]?.childIds.indexOf(targetNode.id);
    updatedNodes[targetParentNode.id] = targetParentNode;
    updatedNodes[targetParentNode.id].childIds = [
      ...updatedNodes[targetParentNode.id].childIds.slice(
        0,
        indexOfTargetInTargetParentChildIds,
      ),
      sourceNode.id,
      ...updatedNodes[targetParentNode.id].childIds.slice(
        indexOfTargetInTargetParentChildIds,
      ),
    ];
  }

  return {
    ...treeState,
    orderedIds: updatedOrderedIds,
    normalizedTree: {
      ...normalizedTree,
      ...updatedNodes,
    },
  };
};

const addChildNodes = <InputTreeNode extends DefaultInputTreeNode>({
  treeState,
  normalizedTree,
  targetId,
  newChildNodes: newChildNodesProp,
  newChildExpandedIds,
  getIdFromNode,
  getLabelFromNode,
  getChildrenFromNode,
}: {
  treeState: TreeState<InputTreeNode>;
  normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>>;
  targetId: IdType;
  newChildNodes: InputTreeNode[];
  newChildExpandedIds?: Record<IdType, boolean>;
  getIdFromNode: ((node: InputTreeNode) => IdType) | undefined;
  getLabelFromNode: ((node: InputTreeNode) => string) | undefined;
  getChildrenFromNode:
    | ((node: InputTreeNode) => InputTreeNode[] | undefined)
    | undefined;
}) => {
  const newChildNodes = normalizeTreeInput<InputTreeNode>({
    denormalizedTree: newChildNodesProp,
    expandedIds: newChildExpandedIds,
    getIdFromNode,
    getLabelFromNode,
    getChildrenFromNode,
  });

  const prePath = [...normalizedTree[targetId].path, targetId];
  const newChildIds: IdType[] = [];

  Object.entries(newChildNodes.normalizedTree).forEach(([id, el]) => {
    if (!el.path.length) {
      newChildIds.push(id);
    }
    el.path = [...prePath, ...el.path];
  });
  normalizedTree[targetId].childIds = newChildIds;

  const loc = treeState.orderedIds.indexOf(targetId);
  const updatedOrderedIds = [
    ...treeState.orderedIds.slice(0, loc + 1),
    ...newChildNodes.orderedIds,
    ...treeState.orderedIds.slice(loc + 1),
  ];
  return {
    ...treeState,
    orderedIds: updatedOrderedIds,
    expandedIds: {
      ...treeState.expandedIds,
      ...newChildNodes.expandedIds,
    },
    normalizedTree: {
      ...normalizedTree,
      ...newChildNodes.normalizedTree,
    },
  };
};

const useTreeReducer = <
  InputTreeNode extends DefaultInputTreeNode,
  CustomAction extends DefaultAction = BaseAction<InputTreeNode>,
>({
  denormalizedTree,
  initialExpandAll,
  initialHighlightedId,
  initialExpandedIds,
  getIdFromNode,
  getLabelFromNode,
  getChildrenFromNode,
  customReducer,
}: UseTreeReducer<InputTreeNode, CustomAction>) => {
  const {
    normalizedTree: initialNormalizedTree,
    orderedIds: initialOrderedIds,
    expandedIds: internalExpandedIds,
  } = useMemo(
    () =>
      normalizeTreeInput<InputTreeNode>({
        denormalizedTree,
        initialExpandAll,
        expandedIds: initialExpandedIds,
        getIdFromNode,
        getLabelFromNode,
        getChildrenFromNode,
      }),
    [
      getChildrenFromNode,
      getIdFromNode,
      getLabelFromNode,
      initialExpandAll,
      denormalizedTree,
      initialExpandedIds,
    ],
  );

  const initialTreeState: TreeState<InputTreeNode> = useMemo(
    () => ({
      normalizedTree: initialNormalizedTree,
      highlightedId: initialHighlightedId,
      orderedIds: initialOrderedIds,
      expandedIds: initialExpandedIds || internalExpandedIds,
    }),
    [
      initialNormalizedTree,
      initialHighlightedId,
      initialOrderedIds,
      initialExpandedIds,
      internalExpandedIds,
    ],
  );

  const baseReducer = useCallback(
    (
      treeState: TreeState<InputTreeNode>,
      action: BaseAction<InputTreeNode>,
    ): TreeState<InputTreeNode> => {
      const { normalizedTree } = treeState;

      switch (action.type) {
        case TREE_ACTIONS.expandNode: {
          return expandCollapseNode(
            treeState,
            action.payload.id,
            normalizedTree,
            true,
            expandNodeTraverse,
          );
        }
        case TREE_ACTIONS.expandNodeRecursive: {
          return expandCollapseNode(
            treeState,
            action.payload.id,
            normalizedTree,
            true,
            expandNodeTraverseRecursive,
          );
        }
        case TREE_ACTIONS.collapseNode: {
          return expandCollapseNode(
            treeState,
            action.payload.id,
            normalizedTree,
            false,
            collapseNodeTraverse,
          );
        }
        case TREE_ACTIONS.collapseNodeRecursive: {
          return expandCollapseNode(
            treeState,
            action.payload.id,
            normalizedTree,
            false,
            collapseNodeTraverseRecursive,
          );
        }
        case TREE_ACTIONS.collapseAllNodes: {
          return collapseAllNodes({ treeState, normalizedTree });
        }
        case TREE_ACTIONS.highlightNode: {
          return highlightNode({ treeState, nodeId: action.payload.id });
        }
        case TREE_ACTIONS.moveDown: {
          return moveDown({
            treeState,
            normalizedTree,
            nodeId: action.payload.id,
          });
        }
        case TREE_ACTIONS.moveUp: {
          return moveUp({
            treeState,
            normalizedTree,
            nodeId: action.payload.id,
          });
        }
        case TREE_ACTIONS.filterNodes: {
          return filterNodes({
            treeState,
            normalizedTree,
            nodeIds: action.payload.ids,
          });
        }
        case TREE_ACTIONS.regenerateTree: {
          const newHighlightedId =
            action.payload.highlightedId || treeState.highlightedId;
          const inputExpandedIds =
            action.payload.expandedIds || treeState.expandedIds;
          const newTreeData = normalizeTreeInput({
            denormalizedTree: action.payload.denormalizedTree,
            initialExpandAll: action.payload.initialExpandAll,
            expandedIds: inputExpandedIds,
            getIdFromNode,
            getLabelFromNode,
            getChildrenFromNode,
          });
          if (
            arraysEqual(newTreeData.orderedIds, treeState.orderedIds) &&
            newTreeData.expandedIds === treeState.expandedIds &&
            newHighlightedId === treeState.highlightedId &&
            normalizedTreesEqual(
              newTreeData.normalizedTree,
              treeState.normalizedTree,
              newTreeData.orderedIds,
            )
          ) {
            return treeState;
          }
          return {
            ...treeState,
            highlightedId: newHighlightedId,
            ...newTreeData,
          };
        }
        case TREE_ACTIONS.moveNode: {
          return moveNode({
            treeState,
            normalizedTree,
            ...action.payload,
          });
        }
        case TREE_ACTIONS.addChildNodes: {
          return addChildNodes({
            treeState,
            normalizedTree,
            getIdFromNode,
            getLabelFromNode,
            getChildrenFromNode,
            ...action.payload,
          });
        }
        default: {
          if (customReducer) {
            return treeState;
          }

          throw new Error(
            `Action not implemented: ${JSON.stringify(
              action,
            )}\n\tIf you need to extend the tree state with custom actions you can pass a 'reducer' function to useTree
          `,
          );
        }
      }
    },
    [customReducer, getChildrenFromNode, getIdFromNode, getLabelFromNode],
  );

  const compositeReducer = useCallback(
    (
      treeState: TreeState<InputTreeNode>,
      action: BaseAction<InputTreeNode> | CustomAction,
    ): TreeState<InputTreeNode> => {
      const newState = customReducer
        ? customReducer(
            baseReducer(treeState, action as BaseAction<InputTreeNode>),
            action,
          )
        : baseReducer(treeState, action as BaseAction<InputTreeNode>);

      if (
        newState.normalizedTree === treeState.normalizedTree &&
        newState.orderedIds === treeState.orderedIds &&
        newState.expandedIds === treeState.expandedIds &&
        newState.highlightedId === treeState.highlightedId
      ) {
        return treeState;
      }
      return newState;
    },
    [customReducer, baseReducer],
  );

  const [state, dispatch] = useReducer(compositeReducer, initialTreeState);
  return { state, dispatch };
};

export default useTreeReducer;
